import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ControlContainer, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { TranslateService } from 'src/app/services/translate.service';

@Component({
  selector: 'form-error',
  templateUrl: `./form-error.component.html`,
  styleUrls: [`./form-error.component.scss`],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FormErrorComponent implements OnInit {
  controlContainer = inject(ControlContainer);

  private readonly translateSvc = inject(TranslateService);

  form: UntypedFormGroup | undefined;

  //  the input's formControl name whose error we want to display
  @Input() name: string | undefined;

  //  if set, allows to only listen for a specific error
  @Input() errorName: string | undefined;

  @Input() id: string;

  @Input() customMessage: { [errorKey: string]: string };

  ngOnInit(): void {
    /**
     * must wait for the parent constructor's to be done,
     * before we can get the form object
     */
    this.form = this.controlContainer.control as UntypedFormGroup;
  }

  /**
   * Determines whether the show the error
   * will only return true if the error has been interacted
   * with by the user
   * @returns boolean
   */
  hasErrors(): boolean {
    const control = this.name ? this.form?.get(this.name) : this.form;
    if (!control) {
      return false;
    }
    return !!control.errors && (control.touched || control.dirty);
  }

  /**
   * Gets errors from field and returns array of human readable
   * errors to be displayed to the user
   * @returns string[]
   */
  getErrors(): string[] {
    const control = this.name ? this.form?.get(this.name) : this.form;
    if (!control?.errors) {
      return [];
    }
    if (this.errorName) {
      return control.errors[this.errorName]
        ? this.makeErrorArray({ [this.errorName]: control.errors[this.errorName] })
        : [];
    }
    return this.makeErrorArray(control.errors);
  }

  /**
   * Builds human readable error array from ValidationError Object
   * @param errors ValidationErrors
   * @returns string[]
   */
  makeErrorArray(errors: ValidationErrors) {
    const errorText: { [key: string]: string } = {
      required: this.translateSvc.translate('VALIDATION.required'),
      minlength: `${this.translateSvc.translate('VALIDATION.minlength')} ${errors['minlength']?.requiredLength}`,
      maxlength: `${this.translateSvc.translate('VALIDATION.maxlength')} ${errors['maxlength']?.requiredLength}`,
      pattern: this.translateSvc.translate('VALIDATION.pattern'),
      min: `${this.translateSvc.translate('VALIDATION.min')} ${errors['min']?.min?.toLocaleString()}`,
      max: `${this.translateSvc.translate('VALIDATION.max')} ${errors['max']?.max?.toLocaleString()}`,
      isInteger: this.translateSvc.translate('VALIDATION.isInteger'),
      decimal: `${this.translateSvc.translate('VALIDATION.decimal')} ${errors['decimal']?.toLocaleString()}`,
      number: `${this.translateSvc.translate('VALIDATION.number')}`,
    };

    const _errors = [];
    if (errors?.hasOwnProperty('matDatepickerParse')) {
      _errors.push(errorText['matDatepickerParse'] || 'This field is invalid');
    } else {
      for (const key in errors) {
        if (
          !key.includes('matDatepicker') &&
          !key.includes('matEndDate') &&
          !key.includes('matStartDate')
        ) {
          const serverError = key == 'serverError' ? errors[key] : 'This field is invalid';
          _errors.push(this.customMessage?.[key] || errorText[key] || serverError);
        }
      }
    }
    return _errors;
  }
}
